import { HttpContext } from '@angular/common/http';
import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AssistantBanner, PersonalAssistantBannerDataService } from '@ffd/api-client';
import { selectCurrentUserProfile } from '@libs/store';
import { DISABLE_GLOBAL_ERROR_TOASTS } from '@libs/utils';
import { Store } from '@ngrx/store';
import { catchError, distinctUntilChanged, EMPTY, map, Observable, of, switchMap, tap } from 'rxjs';

/** The saved welcome text; a `null` field means the default translation. */
export interface AssistantWelcomeText {
  title: string | null;
  description: string | null;
}

const DEFAULT_WELCOME_TEXT: AssistantWelcomeText = { title: null, description: null };

@Injectable({ providedIn: 'root' })
export class AssistantBannerService {
  private readonly personalAssistantBannerDataService = inject(PersonalAssistantBannerDataService);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  private readonly bannerSignal = signal<AssistantWelcomeText | null>(null);
  private initialized = false;

  /** `null` until the request has resolved, so the card never shows the default text first and then the custom text. */
  public readonly banner = this.bannerSignal.asReadonly();
  public readonly isLoaded = computed(() => this.bannerSignal() !== null);

  // Loaded once per logged-in user: a logout and a login do not reload the page, so the next user must not see the
  // text loaded for the previous one. Both layouts call this, and root subscriptions must not stack up.
  public init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.store
      .select(selectCurrentUserProfile)
      .pipe(
        map((profile) => profile?.id),
        distinctUntilChanged(),
        tap(() => this.bannerSignal.set(null)),
        switchMap((userId) => (userId ? this.loadBanner() : EMPTY)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((banner) => this.bannerSignal.set(banner));
  }

  private loadBanner(): Observable<AssistantWelcomeText> {
    return (
      this.personalAssistantBannerDataService
        // The default text is a valid banner, so a failed request shows no error toast to the user
        .getAssistantBanner('body', false, { context: new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true) })
        .pipe(
          map(({ title, description }: AssistantBanner) => ({ title, description })),
          catchError((error) => {
            console.error('Loading the assistant banner failed, the default text is shown', error);
            return of(DEFAULT_WELCOME_TEXT);
          }),
        )
    );
  }
}
